/**
 * User Activity Page
 *
 * Adoption report: who is using the Inspector Tool over 24h / 7d / 30d
 * windows, split by role, with a per-user drill-down timeline. Views come
 * from the user_activity capture; actions are blended from audit_logs
 * exception events (see docs/plans/pending/user-activity-tracking.md).
 */

import React, { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Download as DownloadIcon, QueryStats as QueryStatsIcon } from '@mui/icons-material'
import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'
import BaseDataTable from '../../../components/shared/BaseDataTable'
import ReportFilterCard from '../../../components/shared/filters/ReportFilterCard'
import { FilterKey } from '../../../components/shared/filters/types'
import { useReportFilters } from '../../../components/shared/filters'
import { useDataFetching } from '../../../hooks/useDataFetching'
import { poolwerxColors } from '../../../theme/colors'
import {
  activityApi,
  ActivitySummaryResponse,
  ActivityUserRow,
  ActivityUsersResponse,
  ActivityWindow,
  WINDOW_LABELS,
} from '../../../services/activityApi'
import { useDebounce } from '../../../hooks/useDebounce'
import { ROLE_ORDER, getRoleDisplayName } from '../../../utils/roleDisplay'
import {
  ActivitySummaryCards,
  ActivityTimelineDrawer,
  createActivityUserColumns,
} from './components'

const ENABLED_FILTERS: FilterKey[] = ['textSearch']
const PER_PAGE = 25

const UserActivity: React.FC = () => {
  const [drawerUser, setDrawerUser] = useState<ActivityUserRow | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState('window_last_seen_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const {
    filters,
    updateFilter,
    clearFilters: clearSharedFilters,
    hasActiveFilters: hasSharedActiveFilters,
  } = useReportFilters({ enabledFilters: ENABLED_FILTERS, syncToUrl: true })

  // Custom filters, URL-synced so views are bookmarkable (AuditLogs pattern).
  const [searchParams, setSearchParams] = useSearchParams()
  const [windowFilter, setWindowFilter] = useState<ActivityWindow>(
    () => (searchParams.get('window') as ActivityWindow) || '30d'
  )
  const [roleFilter, setRoleFilter] = useState<string>(() => searchParams.get('role') || '')
  const [marketFilter, setMarketFilter] = useState<string>(() => searchParams.get('market') || '')
  const [fpFilter, setFpFilter] = useState<string>(() => searchParams.get('fp') || '')

  const syncParam = useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set(key, value)
          else next.delete(key)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const setWindow = useCallback(
    (v: ActivityWindow) => {
      setWindowFilter(v)
      syncParam('window', v === '30d' ? '' : v)
      setPage(1)
    },
    [syncParam]
  )
  const setRole = useCallback(
    (v: string) => {
      setRoleFilter(v)
      syncParam('role', v)
      setPage(1)
    },
    [syncParam]
  )
  const setMarket = useCallback(
    (v: string) => {
      setMarketFilter(v)
      syncParam('market', v)
      setPage(1)
    },
    [syncParam]
  )
  const setFp = useCallback(
    (v: string) => {
      setFpFilter(v)
      syncParam('fp', v)
      setPage(1)
    },
    [syncParam]
  )

  const hasActiveFilters =
    hasSharedActiveFilters || Boolean(roleFilter) || Boolean(marketFilter) || Boolean(fpFilter)

  // Debounce the free-text inputs so typing doesn't fire a server
  // aggregate query per keystroke.
  const debouncedFp = useDebounce(fpFilter, 300)
  const debouncedSearch = useDebounce(filters.textSearch, 300)

  const apiFilters = useMemo(
    () => ({
      window: windowFilter,
      role: roleFilter || undefined,
      market: marketFilter || undefined,
      fp: debouncedFp.trim() || undefined,
      search: debouncedSearch || undefined,
    }),
    [windowFilter, roleFilter, marketFilter, debouncedFp, debouncedSearch]
  )

  const { data: summary } = useDataFetching<ActivitySummaryResponse>({
    fetchFn: async () => activityApi.getSummary(),
    dependencies: [],
  })

  const { data: usersData, loading, error } = useDataFetching<ActivityUsersResponse>({
    fetchFn: async () => activityApi.getUsers(apiFilters),
    dependencies: [apiFilters],
  })

  const sortedUsers = useMemo(() => {
    const users = [...(usersData?.users || [])]
    users.sort((a, b) => {
      const av = (a as any)[sortField]
      const bv = (b as any)[sortField]
      // Null handling sits OUTSIDE the direction flip so "Never" rows sort
      // last in both directions.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv))
      return sortDirection === 'asc' ? cmp : -cmp
    })
    return users
  }, [usersData, sortField, sortDirection])

  const pagedUsers = useMemo(
    () => sortedUsers.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [sortedUsers, page]
  )

  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field)
    setSortDirection(direction)
    setPage(1)
  }, [])

  const handleClearFilters = () => {
    clearSharedFilters()
    setRole('')
    setMarket('')
    setFp('')
    setPage(1)
  }

  const handleRowClick = useCallback((row: ActivityUserRow) => {
    setDrawerUser(row)
    setDrawerOpen(true)
  }, [])

  const exportExcel = async () => {
    try {
      setExportError(null)
      await activityApi.exportExcel(apiFilters)
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Excel export failed'
      setExportError('Failed to export user activity: ' + message)
    }
  }

  const columns = useMemo(() => createActivityUserColumns(), [])
  const displayError = error || exportError

  return (
    <>
      <AdminPageHeader pageKey="userActivity" />

      {displayError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setExportError(null)}>
          {displayError}
        </Alert>
      )}

      <ActivitySummaryCards summary={summary || null} />

      <ReportFilterCard
        filters={filters}
        enabledFilters={ENABLED_FILTERS}
        onFilterChange={(key, value) => {
          updateFilter(key as keyof typeof filters, value)
          setPage(1)
        }}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        loading={loading}
        exportButton={
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportExcel}
            size="small"
            sx={{
              borderColor: '#E6E8EA',
              color: '#1F2A2E',
              '&:hover': { borderColor: poolwerxColors.teal, backgroundColor: '#E6F4F5' },
            }}
          >
            Export Excel
          </Button>
        }
        extraFilters={
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Window</InputLabel>
              <Select
                value={windowFilter}
                label="Window"
                onChange={(e) => setWindow(e.target.value as ActivityWindow)}
              >
                {(Object.entries(WINDOW_LABELS) as [ActivityWindow, string][]).map(
                  ([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Role</InputLabel>
              <Select value={roleFilter} label="Role" onChange={(e) => setRole(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {ROLE_ORDER.map((role) => (
                  <MenuItem key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Market</InputLabel>
              <Select
                value={marketFilter}
                label="Market"
                onChange={(e) => setMarket(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="au">AU</MenuItem>
                <MenuItem value="nz">NZ</MenuItem>
                <MenuItem value="us">US</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="FP Code"
              value={fpFilter}
              onChange={(e) => setFp(e.target.value)}
              sx={{ width: 120 }}
            />
          </Stack>
        }
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {sortedUsers.length > 0
          ? `${sortedUsers.length} users - click a row for the activity timeline`
          : 'No users to show'}
      </Typography>

      <BaseDataTable
        variant="enhanced"
        title="Users"
        titleIcon={<QueryStatsIcon />}
        columns={columns}
        data={pagedUsers}
        loading={loading}
        emptyMessage={
          hasActiveFilters ? 'No users match your current filters' : 'No users found'
        }
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        pagination={{
          page,
          perPage: PER_PAGE,
          total: sortedUsers.length,
          onPageChange: setPage,
          showFirstLast: true,
          size: 'large',
        }}
        getRowKey={(row) => row.user_id}
        onRowClick={handleRowClick}
        hover
      />

      <ActivityTimelineDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userId={drawerUser?.user_id ?? null}
        userName={drawerUser?.name}
      />
    </>
  )
}

export default UserActivity
